import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Badge, statusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeftIcon, WrenchIcon, CalendarCheckIcon, TrashIcon, SnowflakeIcon } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { trailerTypeBadge } from "@/pages/Trailers";

export default function TrailerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [trailer, setTrailer] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [services, setServices] = useState([]);

  const load = async () => {
    const [t, r, s] = await Promise.all([
      api.get(`/trailers/${id}`),
      api.get(`/repairs?trailer_id=${id}`),
      api.get(`/services?trailer_id=${id}`),
    ]);
    setTrailer(t.data);
    setRepairs(r.data);
    setServices(s.data);
  };

  useEffect(() => { load(); }, [id]);

  const del = async () => {
    if (!window.confirm("Delete this trailer and all its history?")) return;
    await api.delete(`/trailers/${id}`);
    toast.success("Trailer deleted");
    nav("/trailers");
  };

  if (!trailer) return <div className="p-12 font-mono text-sm text-slate-500">LOADING...</div>;

  const sb = statusBadge(trailer.status);
  const tb = trailerTypeBadge(trailer.trailer_type);
  const total_repair_cost = repairs.reduce((sum, r) => sum + Number(r.total_cost || 0), 0);
  const isReefer = trailer.trailer_type === "reefer";

  return (
    <div data-testid="trailer-detail-page">
      <PageHeader
        eyebrow={`Unit ${trailer.unit_number}`}
        title={`${trailer.year || ""} ${trailer.make} ${trailer.model || ""}`.trim()}
        subtitle={`VIN/Serial: ${trailer.vin}`}
      >
        <Link to="/trailers" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="back-to-trailers">
          <ArrowLeftIcon size={14} /> Back to trailers
        </Link>
        {user?.role === "admin" && (
          <Button variant="outline" onClick={del} className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid="delete-trailer">
            <TrashIcon size={16} weight="bold" className="mr-2" /> Delete
          </Button>
        )}
      </PageHeader>

      <div className="p-8 space-y-6">
        <div className="bg-white border border-slate-300 rounded-sm p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            <Spec label="Type"><Badge tone={tb.tone}>{isReefer && <SnowflakeIcon size={10} weight="bold" />}{tb.label}</Badge></Spec>
            <Spec label="Status"><Badge tone={sb.tone}>{sb.label}</Badge></Spec>
            <Spec label="Length" value={trailer.length_ft ? `${trailer.length_ft} ft` : "—"} mono />
            <Spec label="License plate" value={trailer.license_plate || "—"} mono />
            <Spec label="Year" value={trailer.year || "—"} mono />
            <Spec label="Total repairs" value={`$${total_repair_cost.toLocaleString()}`} mono />
          </div>

          {isReefer && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <SnowflakeIcon size={16} weight="bold" className="text-blue-700" />
                <div className="section-eyebrow">/// Reefer unit</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <Spec label="Reefer make" value={trailer.reefer_make || "—"} />
                <Spec label="Reefer model" value={trailer.reefer_model || "—"} />
                <Spec label="Reefer serial" value={trailer.reefer_serial || "—"} mono />
                <Spec label="Engine hours" value={trailer.reefer_engine_hours != null ? Number(trailer.reefer_engine_hours).toLocaleString() : "—"} mono />
              </div>
            </div>
          )}

          {trailer.notes && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Notes</div>
              <p className="text-sm text-slate-700">{trailer.notes}</p>
            </div>
          )}
        </div>

        <Tabs defaultValue="repairs">
          <TabsList className="rounded-sm" data-testid="trailer-tabs">
            <TabsTrigger value="repairs" data-testid="tab-repairs">
              <WrenchIcon size={14} weight="bold" className="mr-1.5" /> Repairs ({repairs.length})
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">
              <CalendarCheckIcon size={14} weight="bold" className="mr-1.5" /> Services ({services.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="repairs">
            <div className="bg-white border border-slate-300 rounded-sm">
              {repairs.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">No repair records yet.</div>
              ) : (
                <table className="fleet-table">
                  <thead>
                    <tr><th>Date</th><th>Type</th><th>Title</th><th>Mechanic / Shop</th><th>Engine hrs</th><th>Parts</th><th>Bill</th><th>Total</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {repairs.map((r) => {
                      const sb = statusBadge(r.status);
                      const isOutside = r.repair_type === "outside";
                      return (
                        <tr key={r.id} data-testid={`repair-row-${r.id}`}>
                          <td className="font-mono">{r.repair_date?.slice(0, 10)}</td>
                          <td><Badge tone={isOutside ? "warning" : "info"}>{isOutside ? "Outside" : "In-house"}</Badge></td>
                          <td className="font-semibold">{r.title}<div className="text-[10px] text-slate-500 capitalize font-mono">{r.category}</div></td>
                          <td className="text-xs">{r.mechanic || "—"}</td>
                          <td className="font-mono tabular-nums">{r.engine_hours_at_repair != null ? Number(r.engine_hours_at_repair).toLocaleString() : "—"}</td>
                          <td className="font-mono tabular-nums">${Number(r.parts_cost).toLocaleString()}</td>
                          <td className="font-mono tabular-nums">{isOutside ? `$${Number(r.shop_bill || 0).toLocaleString()}` : "—"}</td>
                          <td className="font-mono tabular-nums font-bold">${Number(r.total_cost).toLocaleString()}</td>
                          <td><Badge tone={sb.tone}>{sb.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="services">
            <div className="bg-white border border-slate-300 rounded-sm">
              {services.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">No scheduled services yet.</div>
              ) : (
                <table className="fleet-table">
                  <thead>
                    <tr><th>Service</th><th>Last Date</th><th>Last Hours</th><th>Next Due Date</th><th>Next Due Hours</th><th>Cost</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {services.map((s) => {
                      const sb = statusBadge(s.status);
                      return (
                        <tr key={s.id} data-testid={`service-row-${s.id}`}>
                          <td className="capitalize font-semibold">{s.service_type.replace(/_/g, " ")}</td>
                          <td className="font-mono">{s.last_service_date?.slice(0, 10) || "—"}</td>
                          <td className="font-mono tabular-nums">{s.last_service_engine_hours != null ? Number(s.last_service_engine_hours).toLocaleString() : "—"}</td>
                          <td className="font-mono">{s.next_due_date?.slice(0, 10) || "—"}</td>
                          <td className="font-mono tabular-nums">{s.next_due_engine_hours != null ? Number(s.next_due_engine_hours).toLocaleString() : "—"}</td>
                          <td className="font-mono tabular-nums">${Number(s.cost || 0).toLocaleString()}</td>
                          <td><Badge tone={sb.tone}>{sb.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Spec({ label, value, children, mono }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>
      <div className={`text-sm font-semibold text-slate-900 ${mono ? "font-mono tabular-nums" : ""}`}>
        {children || value}
      </div>
    </div>
  );
}
